import React, { useState, useRef } from 'react';
import SignatureCanvas from './SignatureCanvas';

const YELLOW = '#FFD000';
const BLACK  = '#0a0a0a';
const RED    = '#cc0000';

const CONTRACT_CLAUSES = [
  {
    heading: '1. Nature of Engagement',
    body: 'The Contractor is engaged as an independent contractor and not as an employee, partner, or agent of Acts Car Detailing ("the Company"). The Contractor retains control over the manner and method of performing the services and is responsible for their own tax obligations, superannuation, and insurance.',
  },
  {
    heading: '2. Services',
    body: 'The Contractor agrees to provide car detailing, buffing, and related services as directed by the Company on an as-needed basis. The Company does not guarantee a minimum number of engagements or hours.',
  },
  {
    heading: '3. Payment',
    body: "The Contractor will be paid the agreed rate for each completed job as confirmed in the Company's job management system. Payment will be made weekly by electronic funds transfer to the bank account nominated below. The Contractor is responsible for invoicing the Company (or using the provided app) and for all applicable taxes, including GST if registered.",
  },
  {
    heading: '4. Equipment & Expenses',
    body: 'Unless otherwise agreed in writing, the Contractor is responsible for providing all tools and equipment necessary to perform the services. The Company will supply chemicals and materials required specifically for dealership jobs.',
  },
  {
    heading: '5. Confidentiality',
    body: "The Contractor agrees to keep confidential all customer information, pricing, and business processes of Acts Car Detailing and its dealership clients. This obligation survives the termination of this agreement.",
  },
  {
    heading: '6. Termination',
    body: "Either party may end this engagement at any time with 48 hours' written notice. The Company may terminate immediately for gross misconduct, property damage, or breach of confidentiality.",
  },
];

function Field({ label, value, onChange, type = 'text', placeholder = '', required = true }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 5 }}>
        {label}{required && ' *'}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '12px 14px', borderRadius: 10,
          border: '1.5px solid #e0e0e0', fontSize: 15,
          outline: 'none', background: '#fff',
        }}
      />
    </div>
  );
}

export default function ContractorAgreementPage() {
  const [step, setStep]       = useState(1);
  const [agreed, setAgreed]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]       = useState(false);
  const [result, setResult]   = useState(null); // { emailed, admin_email, pdf_base64, pdf_filename }
  const [error, setError]     = useState('');
  const sigRef = useRef(null);

  const [form, setForm] = useState({
    first_name:     '',
    last_name:      '',
    dob:            '',
    email:          '',
    phone:          '',
    abn:            '',
    bsb:            '',
    account_number: '',
  });

  function set(field) {
    return val => setForm(f => ({ ...f, [field]: val }));
  }

  // ── Step 1: Personal Details ───────────────────────────────────────────
  function validateStep1() {
    const { first_name, last_name, dob, email, phone, abn, bsb, account_number } = form;
    if (!first_name.trim() || !last_name.trim() || !dob || !email.trim() || !phone.trim() || !abn.trim() || !bsb.trim() || !account_number.trim()) {
      setError('Please fill in all fields.');
      return false;
    }
    if (!email.includes('@')) { setError('Please enter a valid email address.'); return false; }
    setError('');
    return true;
  }

  // ── Step 3: Sign & Submit ─────────────────────────────────────────────
  async function handleSubmit() {
    if (!agreed) { setError('Please confirm that you agree to the terms.'); return; }
    if (sigRef.current?.isEmpty()) { setError('Please draw your signature before submitting.'); return; }
    setError('');
    setSubmitting(true);

    try {
      const signatureData = sigRef.current.toDataURL();
      const res = await fetch('/api/contractor-agreement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, signature_data: signatureData }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Submission failed');
      setResult(json);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Done screen ───────────────────────────────────────────────────────
  if (done) {
    const adminEmail = result?.admin_email || 'michelcarrascoza@actscardetailing.com.au';
    const emailed    = result?.emailed === true;

    function downloadPdf() {
      if (!result?.pdf_base64) return;
      // base64 → Blob → download. Works offline / regardless of email.
      const bytes = atob(result.pdf_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.pdf_filename || 'contractor-agreement-acts.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    // Pre-filled email to Acts so the contractor can send the downloaded PDF
    // through (mailto can't attach, so we instruct them to attach it).
    const mailto = `mailto:${adminEmail}`
      + `?subject=${encodeURIComponent(`Signed Contractor Agreement — ${form.first_name} ${form.last_name}`)}`
      + `&body=${encodeURIComponent(`Hi,\n\nPlease find my signed contractor agreement attached (download it first, then attach the PDF here).\n\n${form.first_name} ${form.last_name}\n${form.phone || ''}`)}`;

    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: 40, maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: BLACK, margin: '0 0 10px' }}>Agreement Signed!</h2>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, margin: '0 0 20px' }}>
            Thank you, <strong>{form.first_name}</strong>! Your agreement has been saved.
          </p>

          {/* Download — the reliable copy, always available */}
          {result?.pdf_base64 && (
            <button onClick={downloadPdf} style={{
              width: '100%', padding: '13px 18px', borderRadius: 12, border: 'none',
              background: BLACK, color: YELLOW, fontWeight: 900, fontSize: 15,
              cursor: 'pointer', marginBottom: 14,
            }}>
              📥 Download your signed agreement (PDF)
            </button>
          )}

          {/* Email status */}
          {emailed ? (
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: 12, fontSize: 13, color: '#15803D' }}>
              ✓ A copy was emailed to Acts ({adminEmail}){form.email ? <> and to you ({form.email})</> : null}.
            </div>
          ) : (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: 12, fontSize: 13, color: '#92400E', textAlign: 'left' }}>
              <strong>Please send your agreement to Acts:</strong>
              <ol style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
                <li>Tap <strong>Download</strong> above to save the PDF.</li>
                <li><a href={mailto} style={{ color: '#92400E', fontWeight: 700 }}>Email it to {adminEmail}</a> with the PDF attached.</li>
              </ol>
            </div>
          )}

          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 18 }}>
            Questions? Contact{' '}
            <a href={`mailto:${adminEmail}`} style={{ color: RED }}>{adminEmail}</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: BLACK, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: YELLOW, fontSize: 18, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase' }}>
          Acts Car Detailing
        </span>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px 80px' }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: 20, fontWeight: 900, color: BLACK, margin: '0 0 6px', letterSpacing: 1 }}>
            INDEPENDENT CONTRACTOR AGREEMENT
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
            Please complete all steps to sign your agreement.
          </p>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 28 }}>
          {['Personal Details', 'Read Agreement', 'Sign'].map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done   = step > n;
            return (
              <React.Fragment key={n}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: done ? '#16a34a' : active ? BLACK : '#e5e7eb',
                    color: (done || active) ? '#fff' : '#9ca3af',
                    fontWeight: 800, fontSize: 14,
                  }}>
                    {done ? '✓' : n}
                  </div>
                  <span style={{ fontSize: 10, color: active ? BLACK : '#9ca3af', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap' }}>
                    {label}
                  </span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 2, background: step > n ? '#16a34a' : '#e5e7eb', margin: '0 6px', marginBottom: 20 }} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: BLACK, margin: '0 0 20px' }}>Personal Details</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <Field label="First Name"  value={form.first_name}  onChange={set('first_name')} />
              <Field label="Last Name"   value={form.last_name}   onChange={set('last_name')} />
            </div>
            <Field label="Date of Birth" value={form.dob}   onChange={set('dob')}   type="date" />
            <Field label="Email"         value={form.email}  onChange={set('email')} type="email" placeholder="you@example.com" />
            <Field label="Phone"         value={form.phone}  onChange={set('phone')} type="tel"   placeholder="+61 4xx xxx xxx" />

            <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '16px 0' }} />
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 14px', letterSpacing: 0.5 }}>BANK & TAX DETAILS</p>
            <Field label="ABN"            value={form.abn}            onChange={set('abn')}            placeholder="XX XXX XXX XXX" />
            <Field label="BSB"            value={form.bsb}            onChange={set('bsb')}            placeholder="XXX-XXX" />
            <Field label="Account Number" value={form.account_number} onChange={set('account_number')} placeholder="XXXXXXXXX" />

            {error && <p style={{ color: RED, fontSize: 13, margin: '8px 0 0' }}>{error}</p>}

            <button
              onClick={() => { if (validateStep1()) setStep(2); }}
              style={{
                marginTop: 20, width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                background: BLACK, color: YELLOW, fontWeight: 900, fontSize: 15, cursor: 'pointer',
              }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: BLACK, margin: '0 0 4px' }}>Contractor Agreement</h2>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px' }}>Please read all terms carefully before signing.</p>

            <div style={{ maxHeight: 420, overflowY: 'auto', paddingRight: 8 }}>
              {CONTRACT_CLAUSES.map(clause => (
                <div key={clause.heading} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: BLACK, marginBottom: 6 }}>{clause.heading}</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>{clause.body}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <button
                onClick={() => setStep(1)}
                style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #e0e0e0', background: '#f9fafb', color: '#374151', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                ← Back
              </button>
              <button
                onClick={() => setStep(3)}
                style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: BLACK, color: YELLOW, fontWeight: 900, fontSize: 14, cursor: 'pointer' }}
              >
                I've Read It — Sign →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3 ── */}
        {step === 3 && (
          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: BLACK, margin: '0 0 4px' }}>Sign Your Agreement</h2>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 16px' }}>
              Draw your signature in the box below using your finger or mouse.
            </p>

            {/* Confirm summary */}
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#374151' }}>
              <div><strong>{form.first_name} {form.last_name}</strong> &middot; DOB: {form.dob}</div>
              <div style={{ color: '#6b7280', fontSize: 12 }}>{form.email} &middot; {form.phone}</div>
            </div>

            {/* Signature area */}
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <SignatureCanvas ref={sigRef} height={160} />
              <button
                onClick={() => sigRef.current?.clear()}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(255,255,255,0.9)', border: '1px solid #e0e0e0',
                  borderRadius: 6, fontSize: 11, padding: '3px 10px', cursor: 'pointer', color: '#6b7280',
                }}
              >
                Clear
              </button>
            </div>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 16px' }}>Sign above with your finger or mouse</p>

            {/* Agreement checkbox */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, accentColor: BLACK, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                I, <strong>{form.first_name} {form.last_name}</strong>, confirm that I have read and understood the Independent Contractor Agreement and agree to be bound by its terms.
              </span>
            </label>

            {error && <p style={{ color: RED, fontSize: 13, margin: '0 0 10px' }}>{error}</p>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setStep(2)}
                disabled={submitting}
                style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #e0e0e0', background: '#f9fafb', color: '#374151', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 2, padding: '12px', borderRadius: 10, border: 'none',
                  background: submitting ? '#d1d5db' : BLACK,
                  color: submitting ? '#9ca3af' : YELLOW,
                  fontWeight: 900, fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Submitting...' : '✓ Submit Agreement'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
